import { Dialog } from '@progress/kendo-react-dialogs';
import { BackActionDialogContentLayout } from 'app/components/layouts/BackActionDialogContentLayout';
import OperatorDetailsActions from 'app/components/access-sharing/OperatorDetailsActions';
import RevokeAllAccessAction from 'app/components/access-sharing/RevokeAllAccessAction';
import useClickOutside from 'app/hooks/useClickOutside';
import useGetEquipmentData from 'app/hooks/useGetEquipmentData';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SharedItemShareType } from 'services/api/shares';
import { IOperatorDetails } from 'types/IEquipmentEntry';
import { AccessSharingRole } from 'types/entities/AccessSharingRole';
import { SharedUserAccess } from 'app/components/access-sharing/cards/SharedUserAccessCard/types';
import { useFormatter } from 'shared/formatters';

interface IOperatorDetailsDialog {
  onClose: () => void;
  operatorDetails: {
    displayName: string;
    email: string;
    sharedItems: IOperatorDetails[];
  };
  onRevokeSuccess?: () => void;
  isMobile?: boolean;
}

const OperatorDetailsDialog = ({
  onClose,
  operatorDetails,
  isMobile = false,
}: IOperatorDetailsDialog) => {
  const { t } = useTranslation();
  const { equipments, sharesData } = useGetEquipmentData();

  useClickOutside(onClose);

  const { formatDateTime } = useFormatter();

  // Helper function to get item data (equipment or fleet) for a specific item
  const getItem = (item: IOperatorDetails) => {
    if (item.type === SharedItemShareType.EQUIPMENT) {
      return equipments?.find(equipment => equipment.id === item.exposedId);
    } else if (item.type === SharedItemShareType.FLEET) {
      return sharesData?.fleets?.find(
        fleet => fleet.sharedItemDetails.exposedId === item.exposedId,
      );
    }
    return undefined;
  };

  // Helper function to convert IOperatorDetails to SharedUserAccess
  const convertToSharedUserAccess = (
    item: IOperatorDetails,
  ): SharedUserAccess => {
    const [firstName, lastName] = operatorDetails.displayName.split(' ');
    return {
      id: item.exposedAccessSharingRecordId,
      firstName: firstName || '',
      lastName: lastName || '',
      email: operatorDetails.email,
      role: item.role as AccessSharingRole,
      startTime: item.startTime,
      endTime: item.endTime,
    };
  };

  return (
    <Dialog className="operator-details-modal-container">
      <BackActionDialogContentLayout
        withShadow={false}
        className="operator-details-dialog"
        onBack={onClose}
      >
        <div className="operator-details-dialog__title">
          <div className="operator-details-dialog__title-content">
            <h2>
              {t('access_sharing.shared_with.user_access', {
                user: operatorDetails.displayName,
              })}
            </h2>
            <RevokeAllAccessAction
              userEmail={operatorDetails.email}
              userName={operatorDetails.displayName}
              sharedItems={operatorDetails.sharedItems}
            />
          </div>
          <h3>{operatorDetails.email}</h3>
        </div>

        <div className="access-sharing-subcontainer operator-details-subcontainer">
          <div className="access-sharing-subcontainer-body">
            {!isMobile ? (
              <div className="access-sharing-subcontainer-body-table">
                <div className="access-sharing-subcontainer-body-table-header">
                  <div>
                    {t('access_sharing.shared_with.table.has_access_to')}
                  </div>
                  <div>{t('role')}</div>
                  <div>{t('start_time')}</div>
                  <div>{t('end_time')}</div>
                  <div></div>
                </div>
                <div className="access-sharing-subcontainer-body-table-body">
                  {operatorDetails.sharedItems?.map(item => {
                    const equipmentCount = item.details.equipmentCount || 0;
                    return (
                      <div
                        className="access-sharing-table-row"
                        key={item.exposedId}
                      >
                        <div>
                          <div className="shared-item-label">
                            {item.sharedItemLabel}
                          </div>
                          {item.type === SharedItemShareType.EQUIPMENT && (
                            <div className="equipment-details">
                              <div>
                                <span>{t('equipment_model')}</span>
                                <span>{item.details.model}</span>
                              </div>
                              <div>
                                <span>{t('equipment_pin')}</span>
                                <span>{item.details.pinOrSerial}</span>
                              </div>
                            </div>
                          )}
                          {item.type === SharedItemShareType.FLEET && (
                            <div>
                              <div>
                                {equipmentCount > 1 || equipmentCount === 0
                                  ? `${t('number_of_equipment', {
                                      numberOfEquipment: equipmentCount,
                                    })}`
                                  : t(
                                      'access_sharing.shared_with.card.equipment_singular',
                                      {
                                        numberOfEquipment: equipmentCount,
                                      },
                                    )}
                              </div>
                            </div>
                          )}
                        </div>
                        <div>{t(item.role.toLowerCase())}</div>
                        <div>{formatDateTime(item.startTime)}</div>
                        <div>{formatDateTime(item.endTime)}</div>
                        <div>
                          <OperatorDetailsActions
                            exposedAccessSharingRecordId={
                              item.exposedAccessSharingRecordId
                            }
                            shareType={item.type}
                            userEmail={operatorDetails.email}
                            itemLabel={item.sharedItemLabel}
                            item={getItem(item)}
                            userAccess={convertToSharedUserAccess(item)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="access-sharing-subcontainer-body-table-body ">
                {operatorDetails.sharedItems?.map(item => {
                  const equipmentCount = item.details.equipmentCount || 0;
                  return (
                    <div
                      className="access-sharing-table-card operator-details"
                      key={item.exposedId}
                    >
                      <div className="operator-details-card-header">
                        <div className="shared-item-label">
                          <div
                            style={{
                              fontWeight: 700,
                              fontSize: 18,
                              marginBottom: 6,
                            }}
                          >
                            {item.sharedItemLabel}
                          </div>
                          {item.type === SharedItemShareType.FLEET && (
                            <div style={{ fontWeight: 400, fontSize: 14 }}>
                              {equipmentCount > 1 || equipmentCount === 0
                                ? `${t('number_of_equipment', {
                                    numberOfEquipment: equipmentCount,
                                  })}`
                                : t(
                                    'access_sharing.shared_with.card.equipment_singular',
                                    {
                                      numberOfEquipment: equipmentCount,
                                    },
                                  )}
                            </div>
                          )}
                          {item.type === SharedItemShareType.EQUIPMENT && (
                            <div className="row">
                              <div
                                className="col-xxs-6"
                                style={{ fontSize: 14 }}
                              >
                                <span>{t('equipment_model')}</span>
                              </div>
                              <div
                                className="col-xxs-6"
                                style={{ fontWeight: 400, fontSize: 14 }}
                              >
                                <span>{item.details.model}</span>
                              </div>
                              <div
                                className="col-xxs-6"
                                style={{ fontSize: 14 }}
                              >
                                <span>{t('equipment_pin')}</span>
                              </div>
                              <div
                                className="col-xxs-6"
                                style={{ fontWeight: 400, fontSize: 14 }}
                              >
                                <span>{item.details.pinOrSerial}</span>
                              </div>
                            </div>
                          )}
                        </div>

                        <OperatorDetailsActions
                          exposedAccessSharingRecordId={
                            item.exposedAccessSharingRecordId
                          }
                          shareType={item.type}
                          userEmail={operatorDetails.email}
                          itemLabel={item.sharedItemLabel}
                          item={getItem(item)}
                          userAccess={convertToSharedUserAccess(item)}
                        />
                      </div>

                      <div className="operator-details-card-info">
                        <div className="row-4 operator-details-card-info-row">
                          <div className="col-xxs-12">
                            <div className="k-card k-card-horizontal operator-details-card-info-role-item">
                              <span>{t('role')}</span>
                              <span>{t(item.role.toLowerCase())}</span>
                            </div>
                          </div>
                        </div>
                        <div
                          className="row-4 operator-details-card-info-row"
                          style={{ marginTop: 4 }}
                        >
                          <div className="col-xxs-6">
                            <div className="k-card k-card-vertical operator-details-card-info-role-item">
                              <div style={{ fontWeight: 500, fontSize: 14 }}>
                                {t('start_time')}
                              </div>
                              <div
                                style={{
                                  whiteSpace: 'wrap',
                                }}
                              >
                                {formatDateTime(item.startTime) || '-'}
                              </div>
                            </div>
                          </div>
                          <div className="col-xxs-6">
                            <div className="k-card k-card-vertical operator-details-card-info-role-item">
                              <div style={{ fontWeight: 500, fontSize: 14 }}>
                                {t('end_time')}
                              </div>
                              <div>{formatDateTime(item.endTime) || '-'}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </BackActionDialogContentLayout>
    </Dialog>
  );
};

export default OperatorDetailsDialog;
